import React from 'react';

import { Icon, Pressable } from '@rbilabs/universal-components';
import { Platform, StyleSheet } from 'react-native';
import type { ViewStyle } from 'react-native';

import { Box, BoxProps, withTypography } from 'components/ui';
import { getDesignToken, spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

export const Container = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.container, style] });

export const ClickableWrapper = Pressable.withConfig<{ hasAlert: boolean }>(p => ({
  flexDirection: 'row',
  alignItems: 'center',
  height: p.hasAlert ? '$14' : '$12',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  marginRight: '$1',
  zIndex: 1000,
  _web: {
    cursor: 'pointer',
  },
}));

type IconContainerProps = BoxProps & { isDesktop?: boolean };
export const IconContainer = ({ isDesktop, style, ...props }: IconContainerProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.iconContainer, isDesktop && styles.desktopIconContainer, style],
  });

export const DetailsWrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.detailsWrapper, style] });

type DetailsContainerProps = BoxProps & { justifyContent?: ViewStyle['justifyContent'] | null };
export const DetailsContainer = ({ justifyContent, style, ...props }: DetailsContainerProps) =>
  React.createElement(Box, {
    ...props,
    style: [styles.detailsContainer, justifyContent ? { justifyContent } : undefined, style],
  });

export const Heading = withTypography({
  fontFamily: 'heading',
  numberOfLines: 1,
  fontSize: 'md',
  maxFontSizeMultiplier: 1.5,
});

export const Details = withTypography({
  numberOfLines: 1,
  pl: '$2',
  fontSize: 'md',
  pr: '$1',
  maxFontSizeMultiplier: 1.5,
  style: [
    { flex: 1, overflow: 'hidden', maxWidth: '100%' },
    Platform.select({ web: { textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as any }),
  ],
});

export const SmartAlertText = withTypography({
  numberOfLines: 1,
  fontSize: 'xs',
  bold: true,
  color: getDesignToken('text-default'),
  lineHeight: 17,
  maxFontSizeMultiplier: 1.2,
  style: [
    { flex: 1, overflow: 'hidden' },
    Platform.select({ web: { textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as any }),
  ],
});

export const ButtonWrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style });

export const IconStyled = Icon.withConfig({
  size: '5',
  ...hiddenAccessibilityPlatformProps,
});

export const ArrowRightIcon = Icon.withConfig({
  variant: 'back',
  size: {
    base: 'xs',
    md: 'sm',
  },
  mt: {
    base: spacing.$0_5,
    md: '$1',
  },
  style: {
    transform: [{ rotate: '180deg' }],
  },
});

const styles = StyleSheet.create({
  container: { alignItems: 'center', flexDirection: 'row' },
  detailsContainer: { alignItems: 'center', flex: 1, flexDirection: 'row' },
  detailsWrapper: { flex: 1 },
  desktopIconContainer: { marginLeft: spacing.$1 },
  iconContainer: { marginRight: spacing.$2 },
});
